import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles,
    Button,
    DialogContent,
    DialogActions,
    Dialog,
} from '@material-ui/core';
import { useQuery, useMutation } from '@apollo/client';
import { FetchPolicy } from 'utils/enum/Core';
import { Col, Form } from 'react-bootstrap';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import ButtonStyles from 'styles/theme/Button';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
        },
    },
    dialogContent: {
        padding: 10,
    },
    subLabel: {
        fontSize: '12px',
    },
    input: {
        fontSize: '14px',
    },
}));

const AdvertisingCustomUrlDialog = ({
    stockNumber,
    onClose,
    setCustomUrlsUsed,
    isCustomUrlDialogOpen,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        advertisingURL1: null,
        advertisingURL2: null,
    });

    const {
        advertisingURL1,
        advertisingURL2,
    } = state;

    const {
        data,
        loading,
        error,
    } = useQuery(InventoryQuery.GET_ADVERTISING_CUSTOM_URL, {
        variables: {
            stockNumber,
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const calculateUsedCustomUrls = (url1, url2) => {
        let usedUrls = 0;
        if (!StringUtils.isEmpty(url1)) usedUrls += 1;
        if (!StringUtils.isEmpty(url2)) usedUrls += 1;
        return usedUrls;
    };

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data) {
            const urls = data?.getAdvertisingCustomURL;
            if (urls) {
                setCustomUrlsUsed(calculateUsedCustomUrls(urls.advertisingURL1, urls.advertisingURL2));
                setState((prevState) => ({
                    ...prevState,
                    advertisingURL1: urls.advertisingURL1,
                    advertisingURL2: urls.advertisingURL2,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    const [updateURL, { loading: updatingURL }] = useMutation(InventoryMutation.UPDATE_ADVERTISING_CUSTOM_URL, {
        onCompleted: (response) => {
            if (response) {
                setCustomUrlsUsed(calculateUsedCustomUrls(advertisingURL1, advertisingURL2));
                ModalUtils.successMessage(null, 'URLs updated successfully');
            }
        },
        onError: (err) => {
            ModalUtils.errorMessage(null, err.message);
        },
    });

    const onSave = () => {
        updateURL({
            variables: {
                stockNumber,
                advertisingURL1: !StringUtils.isEmpty(advertisingURL1) ? advertisingURL1 : null,
                advertisingURL2: !StringUtils.isEmpty(advertisingURL2) ? advertisingURL2 : null,
            },
        });
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const isValidURL1 = StringUtils.isEmpty(advertisingURL1) || StringUtils.isSecureURL(advertisingURL1);
    const isValidURL2 = StringUtils.isEmpty(advertisingURL2) || StringUtils.isSecureURL(advertisingURL2);
    return (
        <Dialog
            open={isCustomUrlDialogOpen}
            maxWidth="md"
            fullWidth
            disableBackdropClick
            disableEscapeKeyDown
            scroll="paper"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <DialogAppBar
                appBarClassName={classes.AppBar}
                title="Advertising Custom URLs"
                onClose={onClose}
                toolbarSize="md"
            />
            <DialogContent className={classes.dialogContent}>
                <Form.Group as={Col}>
                    <Form.Label className={classes.subLabel}>Custom URL 1</Form.Label>
                    <Form.Control
                        className={clsx(classes.input, !isValidURL1 && 'invalid-field')}
                        type="text"
                        name="advertisingURL1"
                        value={advertisingURL1 ?? ''}
                        onChange={(e) => onChange(e)}
                    />
                </Form.Group>
                <Form.Group as={Col}>
                    <Form.Label className={classes.subLabel}>Custom URL 2</Form.Label>
                    <Form.Control
                        className={clsx(classes.input, !isValidURL2 && 'invalid-field')}
                        type="text"
                        name="advertisingURL2"
                        value={advertisingURL2 ?? ''}
                        onChange={(e) => onChange(e)}
                    />
                </Form.Group>
            </DialogContent>
            <DialogActions>
                <Button
                    size="small"
                    className={classes.containedSecondaryInfo}
                    disabled={updatingURL || !isValidURL1 || !isValidURL2}
                    onClick={onSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AdvertisingCustomUrlDialog.propTypes = {
    stockNumber: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
    setCustomUrlsUsed: PropTypes.func.isRequired,
    isCustomUrlDialogOpen: PropTypes.bool.isRequired,
};

export default AdvertisingCustomUrlDialog;
